import styled from 'styled-components';
import InfoTooltip from '../InfoTooltip';
import { useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '../../../../utils/Function';

type SummaryData = {
  // NPL 요약 스키마
  bid_rate: number | string; // 낙찰률(% 값, 예: "43.4")
  bid_price_rate: number | string; // 낙찰가율(% 값, 예: "56.9")
  claim_price: number | string; // 청구금액(숫자)
  collateral_value_rate: number | string; // 담보가치율(%)
  recovery_rate: number | string; // 회수율(%)
  loss_rate: number | string; // 손실률(%)
  shortfall_rate: number | string; // 회수부족율(%)
  mortgage_amount: number | string;
};

type SummaryCardsProps = {
  court?: string;
  region?: string;
  usage?: string;
  usageDetail?: string;
  date?: { from: Date; to: Date };
  type?: 'week' | 'month' | 'year';
  summaryData?: SummaryData | null;
  isLoading?: boolean;
};

type ViewVals = {
  winRate: string; // 낙찰률
  winPriceRate: string; // 낙찰가율
  claimPrice: number; // 청구금액
  collateralValueRate: string; // 담보가치율
  recoveryRate: string; // 회수율
  lossRate: string; // 손실률
  shortfallRate: string; // 회수부족율
  mortgageAmount: number; // 근저당액
};

const INFO_LIST = [
  {
    title: '낙찰률',
    content:
      '낙찰률은 해당 기간동안 진행 중인 물건 대비 낙찰된 물건의 비율입니다.',
  },
  {
    title: '낙찰가율',
    content:
      '낙찰가율은 해당 기간동안 낙찰된 물건의 감정가 대비 낙찰 가격의 비율입니다.',
  },
  {
    title: '근저당액',
    content:
      '근저당액은 대출 금액에서 담보설정 비율을 곱한 금액으로 채권최고액 입니다.',
  },
  {
    title: '청구금액',
    content:
      '청구금액은은 금융기관이 경매를 통해 회수를 청구한 금액으로 원금에 이자 및 비용이 포함됩니다.',
  },
  {
    title: '담보가치율',
    content:
      '담보가치율은 낙찰가를 원금으로 나눈 비율로 담보 대비 회수 가능성을 나타냅니다. 100% 이상이면 원금 이상 회수가 가능한 상태입니다.',
  },
  {
    title: '회수율',
    content:
      '회수율은 낙찰가를 청구금액으로 나눈 비율로 청구금 대비 실제 회수된 비율을 나타냅니다. 100% 이상이면 청구금 초과회수를 의미합니다.',
  },
  {
    title: '손실률',
    content:
      '손실률은 손실액을 대출원금으로 나눈 비율로 회수하지 못한 비중을 나타냅니다.',
  },
  {
    title: '회수부족율',
    content:
      '회수부족율은 손실액을 대출원금으로 나눈 비율로 담보 대비 회수 부족 정도를 나타냅니다.',
  },
];

const INFO_MAP: Record<string, { title: string; content: string }> =
  Object.fromEntries(
    INFO_LIST.map((i) => [i.title, { title: i.title, content: i.content }]),
  );

// 숫자 파싱 유틸(%, , 포함 문자열 안전 파싱)
const toNum = (v: unknown) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  const n = Number(String(v).replace(/[,%\s]/g, ''));
  return isNaN(n) ? 0 : n;
};
// 퍼센트 표기
const toPercentStr = (n: number) =>
  `${Number(n).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
// 숫자 표기(청구금액 등)
const toNumberStr = (n: number) => Number(n).toLocaleString('ko-KR');

export default function NPLSummaryCards({
  court,
  region,
  usage,
  usageDetail,
  date,
  type,
  summaryData,
  isLoading = false,
}: SummaryCardsProps) {
  const [vals, setVals] = useState<ViewVals | null>(null);

  useEffect(() => {
    if (!summaryData) {
      setVals({
        winRate: '',
        winPriceRate: '',
        claimPrice: 0,
        collateralValueRate: '',
        recoveryRate: '',
        lossRate: '',
        shortfallRate: '',
        mortgageAmount: 0,
      });
      return;
    }

    const bid_rate = toNum(summaryData.bid_rate);
    const bid_price_rate = toNum(summaryData.bid_price_rate);
    const claim_price = toNum(summaryData.claim_price);
    const collateral_value_rate = toNum(summaryData.collateral_value_rate);
    const recovery_rate = toNum(summaryData.recovery_rate);
    const loss_rate = toNum(summaryData.loss_rate);

    // 추가 파싱
    const mortgage_amount = toNum(summaryData.mortgage_amount ?? 0);
    const shortfall_rate = toNum(summaryData.shortfall_rate ?? 0);

    setVals({
      winRate: toPercentStr(bid_rate),
      winPriceRate: toPercentStr(bid_price_rate),
      claimPrice: claim_price,
      collateralValueRate: toPercentStr(collateral_value_rate),
      recoveryRate: toPercentStr(recovery_rate),
      lossRate: toPercentStr(loss_rate),
      shortfallRate: toPercentStr(shortfall_rate),
      mortgageAmount: mortgage_amount,
    });
  }, [summaryData]);

  const V = useMemo<ViewVals>(() => {
    if (!vals) {
      return {
        winRate: '',
        winPriceRate: '',
        claimPrice: 0,
        collateralValueRate: '',
        recoveryRate: '',
        lossRate: '',
        shortfallRate: '',
        mortgageAmount: 0,
      };
    }
    return vals;
  }, [vals]);

  return (
    <Board>
      <Card>
        {' '}
        <Category>
          <CategoryTitle>
            낙찰률
            <InfoTooltip
              title={INFO_MAP['낙찰률'].title}
              content={INFO_MAP['낙찰률'].content}
              offsetX={-70}
            />
          </CategoryTitle>
          <Img $color="#DCF7E8">
            <img src="/assets/images/icon/green_pie.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.winRate
          )}
        </Data>
      </Card>
      <Card>
        <Category>
          <CategoryTitle>
            낙찰가율
            <InfoTooltip
              title={INFO_MAP['낙찰가율'].title}
              content={INFO_MAP['낙찰가율'].content}
            />
          </CategoryTitle>
          <Img $color="#FEEFEF">
            <img src="/assets/images/icon/red_money.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.winPriceRate
          )}
        </Data>
      </Card>

      <Card>
        <Category>
          <CategoryTitle>
            근저당액
            <InfoTooltip
              title={INFO_MAP['근저당액'].title}
              content={INFO_MAP['근저당액'].content}
            />
          </CategoryTitle>
          <Img $color="#FCEED4">
            <img src="/assets/images/icon/orange_home.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            formatCurrency(V.mortgageAmount)
          )}
        </Data>
      </Card>

      <Card>
        <Category>
          <CategoryTitle>
            청구금액
            <InfoTooltip
              title={INFO_MAP['청구금액'].title}
              content={INFO_MAP['청구금액'].content}
            />
          </CategoryTitle>
          <Img $color="#E4EDFD">
            <img src="/assets/images/icon/blue_receipt.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            formatCurrency(V.claimPrice)
          )}
        </Data>
      </Card>

      <Card>
        <Category>
          <CategoryTitle>
            담보가치율
            <InfoTooltip
              title={INFO_MAP['담보가치율'].title}
              content={INFO_MAP['담보가치율'].content}
            />
          </CategoryTitle>
          <Img $color="#F0E8FF">
            <img src="/assets/images/icon/purple_graph.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.collateralValueRate
          )}
        </Data>
      </Card>

      <Card>
        <Category>
          <CategoryTitle>
            회수율
            <InfoTooltip
              title={INFO_MAP['회수율'].title}
              content={INFO_MAP['회수율'].content}
            />
          </CategoryTitle>
          <Img $color="#DCF7E8">
            <img src="/assets/images/icon/green_chart.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.recoveryRate
          )}
        </Data>
      </Card>

      <Card>
        <Category>
          <CategoryTitle>
            손실률
            <InfoTooltip
              title={INFO_MAP['손실률'].title}
              content={INFO_MAP['손실률'].content}
            />
          </CategoryTitle>
          <Img $color="#FEEFEF">
            <img src="/assets/images/icon/red_chart.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.lossRate
          )}
        </Data>
      </Card>

      <Card>
        <Category>
          <CategoryTitle>
            회수부족율
            <InfoTooltip
              title={INFO_MAP['회수부족율'].title}
              content={INFO_MAP['회수부족율'].content}
            />
          </CategoryTitle>
          <Img $color="#E8E9ED">
            <img src="/assets/images/icon/gray_exit.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.shortfallRate
          )}
        </Data>
      </Card>
    </Board>
  );
}

const Board = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  align-self: stretch;
`;

const Card = styled.div`
  display: flex;
  padding: 12px 16px;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  border-radius: 8px;
  background: #fff;
  align-self: stretch;
`;

const WideCard = styled(Card)`
  grid-column: 1 / -1;
`;

const Category = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const CategoryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  color: #424854;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.28px;
`;

const Img = styled.div<{ $color: string }>`
  display: flex;
  padding: 8px;
  align-items: center;
  border-radius: 16px;
  background-color: ${({ $color }) => $color};
  .img {
    width: 16px;
    height: 16px;
  }
`;

const Data = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #0c0e12;
`;

const LoaderImg = styled.img`
  width: 20px;
  height: 20px;
  display: inline-block;
`;
